<template>
  <q-dialog ref="dialogRef" class="modal" persistent @hide="onDialogHide">
    <q-card
      class="q-dialog-plugin tw-flex tw-w-full tw-flex-col !tw-rounded-2xl !tw-shadow-xl"
      style="max-width: 480px"
    >
      <div class="tw-flex tw-items-center tw-gap-4 tw-border-b tw-px-6 tw-py-5">
        <h4
          class="tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-lg tw-font-semibold"
        >
          {{ isEdit ? "FAQ-categorie bewerken" : "Nieuwe FAQ-categorie" }}
        </h4>
        <c-button-icon
          flat
          color="primary"
          icon="fas fa-times"
          @click="onDialogCancel"
        />
      </div>

      <q-form @submit="onSubmit">
        <div class="tw-space-y-5 tw-px-6 tw-py-5">
          <q-input
            v-model.trim="title"
            outlined
            autofocus
            label="Naam"
            :rules="titleRules"
            hide-bottom-space
          />

          <q-select
            v-model="parentUuid"
            outlined
            :options="parentOptions"
            :disable="parentSelectorDisabled"
            option-value="value"
            option-label="label"
            emit-value
            map-options
            clearable
            label="Bovenliggende categorie (optioneel)"
            :hint="
              parentSelectorDisabled
                ? 'Deze categorie heeft zelf subcategorieën en kan geen subcategorie worden.'
                : 'Laat leeg om een hoofdcategorie te maken.'
            "
          />

          <div>
            <div class="tw-mb-2 tw-font-medium">Modules</div>
            <div
              v-if="loading && moduleOptions.length === 0"
              class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600"
            >
              <q-spinner size="16px" />
              Modules laden…
            </div>
            <div v-else class="tw-flex tw-flex-col tw-gap-1">
              <q-checkbox
                v-for="option in moduleOptions"
                :key="option.id"
                :model-value="selectedModuleIds.includes(option.id)"
                :label="option.label"
                @update:model-value="(val) => toggleModule(option.id, val)"
              />
            </div>
            <div
              v-if="showModuleError"
              class="tw-mt-1 tw-text-sm tw-text-negative"
            >
              Selecteer minstens één module.
            </div>
          </div>
        </div>

        <div
          class="tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-border-t tw-px-6 tw-py-4"
        >
          <c-button
            color="transparent"
            text-color="gray"
            label="Annuleren"
            @click="onDialogCancel"
          />
          <c-button
            type="submit"
            color="primary"
            :label="isEdit ? 'Opslaan' : 'Aanmaken'"
            :disable="loading && moduleOptions.length === 0"
          />
        </div>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { useKnowledgeBaseModuleOptions } from "composables/knowledge-base/useKnowledgeBaseModuleOptions";
import type { FaqCategory } from "models/knowledge-base/faq";
import { storeToRefs } from "pinia";
import { useDialogPluginComponent } from "quasar";
import { useFaqStore } from "store/modules/faqStore";
import { computed, onMounted, ref } from "vue";

interface Props {
  category?: FaqCategory | null;
}

interface ParentOption {
  value: string | null;
  label: string;
}

const props = withDefaults(defineProps<Props>(), {
  category: null,
});

defineEmits([...useDialogPluginComponent.emits]);

const { moduleOptions, loading, fetchModuleOptions } =
  useKnowledgeBaseModuleOptions();

const faqStore = useFaqStore();
const { categories } = storeToRefs(faqStore);

onMounted(() => {
  fetchModuleOptions();
});

const isEdit = computed(() => props.category !== null);

const title = ref(props.category?.title ?? "");
const parentUuid = ref<string | null>(props.category?.parent_uuid ?? null);
const selectedModuleIds = ref<number[]>([
  ...(props.category?.module_ids ?? []),
]);
const submitted = ref(false);

const hasChildren = computed<boolean>(() => {
  if (props.category === null) {
    return false;
  }
  return categories.value.some((c) => c.parent_uuid === props.category?.uuid);
});

const parentSelectorDisabled = computed<boolean>(() => hasChildren.value);

const parentOptions = computed<ParentOption[]>(() =>
  categories.value
    .filter((c) => c.parent_uuid === null)
    .filter((c) => c.uuid !== props.category?.uuid)
    .slice()
    .sort((a, b) => a.title.localeCompare(b.title))
    .map<ParentOption>((c) => ({ value: c.uuid, label: c.title })),
);

const showModuleError = computed(
  () => submitted.value && selectedModuleIds.value.length === 0,
);

const titleRules = [
  (v: string) => !!v || "Naam is verplicht",
  (v: string) => v.length >= 5 || "Minimaal 5 tekens",
  (v: string) => v.length <= 255 || "Maximaal 255 tekens",
];

const toggleModule = (moduleId: number, value: boolean) => {
  if (value) {
    if (!selectedModuleIds.value.includes(moduleId)) {
      selectedModuleIds.value = [...selectedModuleIds.value, moduleId];
    }
  } else {
    selectedModuleIds.value = selectedModuleIds.value.filter(
      (id) => id !== moduleId,
    );
  }
};

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const onSubmit = () => {
  submitted.value = true;
  if (selectedModuleIds.value.length === 0) {
    return;
  }
  onDialogOK({
    title: title.value.trim(),
    parent_uuid: parentUuid.value,
    module_ids: [...selectedModuleIds.value].sort((a, b) => a - b),
  });
};
</script>
